import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import lscache from "lscache";
import _ from "lodash";

// redux
import { getKeywords, showAppMessage } from "~/redux/actions/app";

import styles from "./AppHeader.module.css";

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
  >
    <circle
      cx="11"
      cy="11"
      r="7"
    />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

// 与 /search 页共用同一份搜索记录（lscache 的 keywords，最新的一条在数组末尾）
const readHistory = () => {
  const items = lscache.get("keywords");
  return items ? items.slice().reverse() : [];
};

/**
 * 站顶搜索：聚焦时给出「大家在搜」（/keywords 热点词里随机 4 个）和「搜索记录」，
 * 选中即搜索。行为对齐原来的 AppSearch（antd AutoComplete），这里自绘下拉
 */
const AppHeaderSearch = ({ open, onOpenChange }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const wrapRef = useRef(null);
  // 地址栏里的 keyword（?keyword=a&keyword=b 时取不到字符串，按空处理）
  const queryKeyword =
    typeof router.query.keyword === "string" ? router.query.keyword : "";
  const [keyword, setKeyword] = useState(queryKeyword);
  const [hotKeywords, setHotKeywords] = useState([]);
  const [history, setHistory] = useState([]);

  // 跳转/刷新后地址栏带了 keyword，就回填到输入框（输入中不会被覆盖）
  useEffect(() => {
    if (queryKeyword) setKeyword(queryKeyword);
  }, [queryKeyword]);

  const handleSearch = (value = keyword) => {
    const text = _.trim(value);
    if (!text) {
      dispatch(showAppMessage("请输您要搜索的关键字", "info"));
      return;
    }
    onOpenChange(false);
    router.push("/search?keyword=" + encodeURIComponent(text));
  };

  const loadOptions = () => {
    getKeywords()
      .then((items) => setHotKeywords(_.sampleSize(items, 4)))
      .catch(() => setHotKeywords([]));
    setHistory(readHistory());
  };

  const handleClearHistory = () => {
    lscache.remove("keywords");
    setHistory([]);
  };

  const hasOptions = hotKeywords.length > 0 || history.length > 0;

  return (
    <div
      className={styles.searchWrap}
      ref={wrapRef}
    >
      <div className={styles.search}>
        <input
          className={styles.searchInput}
          value={keyword}
          placeholder="请输入关键字"
          onChange={(event) => {
            setKeyword(event.target.value);
            // 输入框有内容变化说明正在输入，把下拉带回来
            onOpenChange(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleSearch();
            if (event.key === "Escape") onOpenChange(false);
          }}
          onFocus={() => {
            onOpenChange(true);
            loadOptions();
          }}
          onBlur={(event) => {
            // 焦点移到面板里（点「清空」等）时不收，只在这一组整体失焦时收
            if (!wrapRef.current?.contains(event.relatedTarget)) {
              onOpenChange(false);
            }
          }}
        />
        <button
          type="button"
          className={styles.searchBtn}
          aria-label="搜索"
          onClick={() => handleSearch()}
        >
          <SearchIcon />
        </button>
      </div>

      {open && hasOptions && (
        // 阻止默认的 mousedown，避免点选项时输入框先失焦、面板被收掉
        <div
          className={styles.searchPanel}
          onMouseDown={(event) => event.preventDefault()}
        >
          {hotKeywords.length > 0 && (
            <div className={styles.searchGroup}>
              <div className={styles.searchGroupTitle}>大家在搜</div>
              {hotKeywords.map((item) => (
                <button
                  key={item.hashid || item.name}
                  type="button"
                  className={styles.searchItem}
                  onClick={() => handleSearch(item.name)}
                >
                  {item.name}
                </button>
              ))}
            </div>
          )}
          {history.length > 0 && (
            <div className={styles.searchGroup}>
              <div className={styles.searchGroupTitle}>
                <span>搜索记录</span>
                <button
                  type="button"
                  className={styles.searchClear}
                  onClick={handleClearHistory}
                >
                  清空
                </button>
              </div>
              {history.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={styles.searchItem}
                  onClick={() => handleSearch(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppHeaderSearch;
